import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";

const CATEGORIES = ["FAQs", "Travel Guides", "University Guides", "Policies", "Packing Lists", "Discount Partner Information", "Student Tips", "Internal Documents", "Custom Articles", "General"];

const Toggle = ({ enabled, onChange, testid }) => (
  <button data-testid={testid} onClick={onChange}
    className={`w-11 h-6 rounded-full transition-colors duration-300 relative shrink-0 ${enabled ? "bg-[#D4AF37]" : "bg-white/10"}`}>
    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${enabled ? "left-6" : "left-1"}`} />
  </button>
);

export default function AdminKnowledge() {
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", category: "FAQs", content: "" });

  const load = useCallback(() => {
    api.get("/admin/knowledge").then((r) => setDocs(r.data)).catch(() => toast.error("Could not load knowledge base"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Title and content are required");
    try {
      if (editing) {
        await api.patch(`/admin/knowledge/${editing}`, form);
        toast.success("Updated - Castles AI will use this immediately");
      } else {
        await api.post("/admin/knowledge", form);
        toast.success("Added to knowledge base");
      }
      setForm({ title: "", category: "FAQs", content: "" });
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <AdminLayout title="Castles AI Knowledge Base">
      <p className="text-slate-400 text-sm mb-8 max-w-2xl">
        Everything here is prioritised by Castles AI before general knowledge. Upload FAQs, guides, policies, packing lists, partner info and internal docs - no coding needed.
      </p>

      <div className="rounded-3xl border border-[#D4AF37]/25 bg-white/[0.03] p-7 mb-8" data-testid="kb-editor">
        <h3 className="text-lg text-white font-display mb-5">{editing ? "Edit document" : "Add new document"}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="kb-title-input" placeholder="Title *" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/70" />
          <select data-testid="kb-category-select" value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none [&>option]:bg-[#0A1633]">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <textarea data-testid="kb-content-input" placeholder="Content - write everything Castles AI should know about this topic... *" value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6}
          className="mt-4 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/70" />
        <div className="mt-4 flex gap-3">
          <button data-testid="kb-save-button" onClick={save}
            className="flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#0A1633] font-semibold px-7 py-3 text-sm transition-colors duration-300 hover:bg-[#e5c558]">
            <Plus className="w-4 h-4" /> {editing ? "Save changes" : "Add to knowledge base"}
          </button>
          {editing && (
            <button data-testid="kb-cancel-button" onClick={() => { setEditing(null); setForm({ title: "", category: "FAQs", content: "" }); }}
              className="rounded-full border border-white/15 px-6 py-3 text-sm text-slate-300 transition-colors duration-300 hover:border-white/40">
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3" data-testid="kb-list">
        {docs.map((d) => (
          <div key={d.doc_id} data-testid={`kb-row-${d.doc_id}`}
            className={`rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-5 flex items-start gap-4 ${!d.enabled ? "opacity-50" : ""}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-white font-medium">{d.title}</p>
                <span className="text-xs rounded-full border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1">{d.category}</span>
              </div>
              <p className="mt-2 text-sm text-slate-500 line-clamp-2">{d.content}</p>
            </div>
            <button data-testid={`kb-edit-${d.doc_id}`}
              onClick={() => { setEditing(d.doc_id); setForm({ title: d.title, category: d.category, content: d.content }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="text-xs rounded-full border border-white/15 px-4 py-2 text-slate-300 transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]">
              Edit
            </button>
            <Toggle testid={`kb-toggle-${d.doc_id}`} enabled={d.enabled}
              onChange={async () => { await api.patch(`/admin/knowledge/${d.doc_id}`, { enabled: !d.enabled }); load(); }} />
            <button data-testid={`kb-delete-${d.doc_id}`}
              onClick={async () => { await api.delete(`/admin/knowledge/${d.doc_id}`); toast.success("Removed"); load(); }}
              className="text-slate-500 transition-colors duration-300 hover:text-red-400 mt-1">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
